import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { AuditLogService } from 'src/app/shared/services/audit-log.service';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';
import { canListCompanies } from 'src/app/shared/utilities/can-list-companies';
import { hasPermission } from 'src/app/shared/utilities/has-permission';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { AttendanceReportsService } from './services/attendance-reports.service';
import { IAttendanceReports, IFilterAttendanceReports } from './data/attendance-reports';
import { CompanyService } from '../companies/services/company.service';
import { ICompany } from '../companies/models/company.interface';
import { AttendantService } from '../filas/services/attendant.service';
import { IAttendant } from '../filas/data/lista.interface';
import { ModalDisclaimerComponent } from './components/modal-disclaimer/modal-disclaimer.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

const DISCLAIMER_KEY = 'disclaimer_attendance_reports_accepted';

@Component({
  selector: 'app-attendance-reports',
  templateUrl: './attendance-reports.component.html',
  styleUrls: ['./attendance-reports.component.scss'],
  providers: [DatePipe]
})
export class AttendanceReportsComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  private readonly destroy$ = new Subject<void>();
  private reportsSub?: Subscription;
  attendanceReports: IAttendanceReports[] = [];
  orderControl = new UntypedFormControl(EOrdenacao.MaisNovos);
  companyControl = new UntypedFormControl();
  attendantControl = new UntypedFormControl();

  listOrder = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: EOrdenacao.MaisNovos,
      label: 'Mais novos'
    },
    {
      value: EOrdenacao.MaisAntigos,
      label: 'Mais antigos'
    }
  ];

  dadosPaginacao = {
    ordenacao: EOrdenacao.MaisNovos,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;
  partnerId?: string;
  accountRole?: string;
  attendantId?: string;
  role?: string;
  acaoInput = new UntypedFormControl();
  range = new UntypedFormGroup({
    start: new UntypedFormControl(null),
    end: new UntypedFormControl(null)
  });
  showCalendar = false;
  selectedDate: string | null = null;
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  companies: ICompany[] = [];
  canFilterByCompany = false;
  canFilterByAttendant = false;
  attendants: IAttendant[] = [];
  selectedAttendant: string | null = null;

  constructor(
    private readonly attendanceReportsService: AttendanceReportsService,
    private readonly sessionService: SessionService,
    private readonly datePipe: DatePipe,
    private readonly companyService: CompanyService,
    private readonly attendantService: AttendantService,
    private readonly matDialog: MatDialog,
    private readonly auditLogService: AuditLogService,
    private readonly router: Router,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.attendantId = this.sessionService.getSession().sid;
    this.companyId = this.sessionService.getSession().companyId;
    this.partnerId = this.sessionService.getSession().partnerId;
    this.accountRole = this.sessionService.getSession().accountRole;
    this.role = this.sessionService.getSession().role;

    // Interno e parceiro sem empresa escolhem a empresa; usuário de empresa vê só a dele. As empresas só vêm
    // com a permissão do Company/ListActive, e os atendentes com attendants.list (Admin passa direto).
    const session = this.sessionService.getSession();
    this.canFilterByCompany = canPickCompany(session) && canListCompanies(session);
    this.canFilterByAttendant =
      canPickCompany(session) &&
      hasPermission([ERoleAdminString.Admin, 'attendants.list'], session.userdata, session.role);

    if (sessionStorage.getItem(DISCLAIMER_KEY)) {
      this.carregarDados();
    } else {
      this.matDialog
        .open(ModalDisclaimerComponent, { width: '500px', panelClass: 'bg-iceberg', disableClose: true })
        .afterClosed()
        .subscribe((aceito: boolean) => {
          if (!aceito) {
            this.router.navigate(['/dash/dashboard']);
            return;
          }
          sessionStorage.setItem(DISCLAIMER_KEY, 'true');
          this.auditLogService.logDisclaimerAccepted(this.sessionService.getSession());
          this.carregarDados();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarDados(): void {
    this.getAttendanceReports();
    if (this.canFilterByCompany) {
      this.getCompanies();
    }
    if (this.canFilterByAttendant) {
      this.getAttendants();
    }

    this.orderControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.getAttendanceReports();
    });
    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getAttendanceReports();
    });
    this.attendantControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getAttendanceReports();
    });
    // Como na tela de solicitações: com as duas datas escolhidas, a lista já reflete o período
    // que vai para o Excel, sem depender do clique na lupa.
    this.range.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ start, end }: { start: Date | null; end: Date | null }) => {
        if (start && end) {
          this.filterStartDate = start;
          this.filterEndDate = end;
          this.dadosPaginacao.paginaSelecionada = 0;
          this.getAttendanceReports();
        }
      });
  }

  // Fonte única do filtro. Enquanto listagem e export montavam o objeto separadamente,
  // o export esquecia o intervalo de datas e trazia todo o histórico.
  private buildFilter(): IFilterAttendanceReports {
    const filter: IFilterAttendanceReports = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    if (this.companyControl.value) {
      filter.companyId = this.companyControl.value;
    }

    if (this.attendantControl.value) {
      filter.selectedAttendant = this.attendantControl.value;
    }

    if (this.partnerId) {
      filter.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filter.accountRole = parseInt(this.accountRole);
    }

    if (this.acaoInput.value) {
      filter.searchText = String(this.acaoInput.value);
    }

    if (this.selectedDate) {
      filter.selectedDate = this.selectedDate;
    }

    if (this.filterStartDate) {
      filter.startDate = this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
    }

    if (this.filterEndDate) {
      const endOfDay = new Date(this.filterEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      filter.endDate = this.datePipe.transform(endOfDay, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
    }

    return filter;
  }

  public getAttendanceReports(): void {
    // Cancela a busca anterior: uma resposta atrasada de outro filtro não sobrescreve a atual.
    this.reportsSub?.unsubscribe();
    this.reportsSub = this.attendanceReportsService
      .getAttendanceReports(this.buildFilter())
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.attendanceReports = res.data;
        this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
        this.dadosPaginacao.itensTotal = res.count!;
      });
  }

  public cleanFilter(): void {
    this.acaoInput.reset();
    this.selectedDate = null;
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.range.reset();
    this.companyControl.reset();
    this.attendantControl.reset();
    this.getAttendanceReports();
  }

  public onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.filterStartDate = event.value;
  }

  public onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.filterEndDate = event.value;
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;
    this.getAttendanceReports();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.getAttendanceReports();
  }

  public downloadExcel(): void {
    this.attendanceReportsService.exportarExcelAttendanceReports(this.buildFilter());
  }

  public getAttendanceByDate(date: Date): void {
    this.selectedDate = this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss.SSS');
    this.getAttendanceReports();
  }

  private getCompanies(): void {
    this.companyService
      .listarEmpresasAtivas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          this.companies = resposta.data;
        },
        error: (erro) => {
          this.companies = [];
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
          }
        }
      });
  }

  public toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  private getAttendants(): void {
    this.attendantService.getAttendants().subscribe((response) => {
      this.attendants = response.data;
    });
  }
}
